// Copyright (c) Ping. All rights reserved.
//
// Base (non-macOS) resolution of the `PingVideoPlayer` surface. Web and any
// other platform have no native AVKit view, so this renders an empty
// placeholder and the staging helper no-ops. Metro on the desktop build
// resolves `PingVideoPlayer.macos.tsx` instead, which wraps the real
// `AVPlayerView`-backed native component.
//
// Keeping a base file here means the shared barrel (`@ping/ui`) and the
// shared `MediaPreviewScreen` can import this module unconditionally without
// crashing the web bundle (`requireNativeComponent` would throw off-native).

import React from 'react';
import { View, type StyleProp, type ViewStyle } from 'react-native';

export interface PingVideoPlayerProps {
  /** Local file path (or `file://` / `http(s)://` URL) to play. */
  readonly source: string;
  /** Begin playback as soon as the item loads (default true). */
  readonly autoplay?: boolean | undefined;
  /** Show the inline transport controls (default true). */
  readonly controls?: boolean | undefined;
  readonly style?: StyleProp<ViewStyle> | undefined;
  readonly testID?: string | undefined;
}

/** No-op placeholder off-macOS (see the `.macos` sibling for the real view). */
export function PingVideoPlayer(props: PingVideoPlayerProps): React.ReactElement {
  const { style, testID } = props;
  return <View style={style} {...(testID !== undefined ? { testID } : {})} />;
}

/** True only when the native AVKit player is linked (always false here). */
export function isPingVideoPlayerLinked(): boolean {
  return false;
}

/**
 * Stage decrypted video bytes to a temp file and resolve its path. No native
 * bridge off-macOS, so this always resolves `null`.
 */
export async function stageVideoTempFile(
  _bytes: Uint8Array,
  _mime: string,
): Promise<string | null> {
  return null;
}
